import { useApolloClient } from '@apollo/client';
import { Divider, Form, message } from 'antd';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import {
    type CreatePayGatePaymentSettingsMutationVariables,
    type CreatePayGatePaymentSettingsMutation,
    CreatePayGatePaymentSettingsDocument,
} from '../../../../api/mutations/createPayGatePaymentSettings';
import { type PayGatePaymentSettingsInput } from '../../../../api/types';
import InputField from '../../../../components/fields/InputField';
import InputNumberField from '../../../../components/fields/InputNumberField';
import PasswordField from '../../../../components/fields/PasswordField';
import useHandleError from '../../../../utilities/useHandleError';
import useValidator from '../../../../utilities/useValidator';
import validator from './validators';

export type AddPayGatePaymentSettingsFormProps = {
    moduleId: string;
    onComplete: () => void;
};

type FormValues = PayGatePaymentSettingsInput;

const initialValues: FormValues = {
    amount: 0,
    encryptionKey: '',
    currency: '',
    displayName: '',
    apiKey: '',
    paymentEndpoint: '',
};

const AddPayGatePaymentSettingsForm = ({ moduleId, onComplete }: AddPayGatePaymentSettingsFormProps) => {
    const { t } = useTranslation(['moduleDetails', 'paygatePaymentModuleDetails']);

    const apolloClient = useApolloClient();

    const onSubmit = useHandleError(
        async (values: FormValues) => {
            message.loading({
                content: t('paygatePaymentModuleDetails:paymentSettingsDrawer.messages.submitting'),
                key: 'primary',
                duration: 0,
            });

            await apolloClient
                .mutate<CreatePayGatePaymentSettingsMutation, CreatePayGatePaymentSettingsMutationVariables>({
                    mutation: CreatePayGatePaymentSettingsDocument,
                    variables: { moduleId, settings: values },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            message.success({
                content: t('paygatePaymentModuleDetails:paymentSettingsDrawer.messages.success'),
                key: 'primary',
            });

            // close the drawer
            onComplete();
        },
        [onComplete, t, apolloClient, moduleId]
    );

    const validate = useValidator(validator());

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit }) => (
                <Form
                    id="addPayGatePaymentSettings"
                    layout="vertical"
                    name="addPayGatePaymentSettings"
                    onSubmitCapture={handleSubmit}
                >
                    <Divider>{t('paygatePaymentModuleDetails:dividers.mainDetails')}</Divider>
                    <InputField
                        {...t('paygatePaymentModuleDetails:paymentSettingsDrawer.fields.displayName', {
                            returnObjects: true,
                        })}
                        name="displayName"
                        required
                    />
                    <InputField
                        {...t('paygatePaymentModuleDetails:paymentSettingsDrawer.fields.currency', {
                            returnObjects: true,
                        })}
                        name="currency"
                    />
                    <InputNumberField
                        {...t('paygatePaymentModuleDetails:paymentSettingsDrawer.fields.amount', {
                            returnObjects: true,
                        })}
                        name="amount"
                    />
                    <Divider>{t('paygatePaymentModuleDetails:dividers.paymentSettings')}</Divider>
                    <InputField
                        {...t('paygatePaymentModuleDetails:paymentSettingsDrawer.fields.apiKey', {
                            returnObjects: true,
                        })}
                        name="apiKey"
                    />
                    <PasswordField
                        {...t('paygatePaymentModuleDetails:paymentSettingsDrawer.fields.encryptionKey', {
                            returnObjects: true,
                        })}
                        name="encryptionKey"
                    />
                    <InputField
                        {...t('paygatePaymentModuleDetails:paymentSettingsDrawer.fields.paymentEndpoint', {
                            returnObjects: true,
                        })}
                        name="paymentEndpoint"
                    />
                </Form>
            )}
        </Formik>
    );
};

export default AddPayGatePaymentSettingsForm;
